
import React, { useState, useEffect } from 'react';
import { WeatherCondition } from '@/types/weather';
import { CloudLightning } from 'lucide-react';
import { motion } from 'framer-motion';

interface WeatherEffectsProps {
  condition: WeatherCondition;
}

const rainDrops = Array.from({ length: 40 }, (_, i) => i);
const snowFlakes = Array.from({ length: 30 }, (_, i) => i);
const fogParticles = Array.from({ length: 20 }, (_, i) => i);

export const WeatherEffects: React.FC<WeatherEffectsProps> = ({ condition }) => {
  const [showLightning, setShowLightning] = useState(false);
  
  useEffect(() => {
    if (condition === 'thunderstorm') {
      const interval = setInterval(() => {
        setShowLightning(true);
        setTimeout(() => setShowLightning(false), 200);
      }, Math.random() * 5000 + 3000);

      return () => clearInterval(interval);
    }
  }, [condition]);
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {(condition === 'rain' || condition === 'heavyRain') && (
        <div className="absolute inset-0">
          {rainDrops.map((i) => (
            <motion.div
              key={`rain-${i}`}
              className={`absolute w-0.5 bg-blue-100/80 rounded-full ${condition === 'heavyRain' ? 'h-10' : 'h-6'}`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                top: ['0%', '100%'],
                opacity: [0, 0.4, 0.8, 0.4, 0],
              }}
              transition={{
                duration: condition === 'heavyRain' ? 0.8 : 1.2,
                repeat: Infinity,
                delay: Math.random() * 2,
                ease: 'linear',
              }}
            />
          ))}
        </div>
      )}
      
      {condition === 'snow' && (
        <div className="absolute inset-0">
          {snowFlakes.map((i) => (
            <motion.div
              key={`snow-${i}`}
              className="absolute w-2 h-2 bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                top: ['0%', '100%'],
                x: [0, Math.random() * 100 - 50],
                opacity: [0, 0.8, 0.4, 0],
              }}
              transition={{
                duration: 6 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'linear',
              }}
            />
          ))}
        </div>
      )}
      
      {condition === 'fog' && (
        <div className="absolute inset-0 overflow-hidden">
          {/* Fog overlay */}
          <div className="absolute inset-0 bg-white/30 backdrop-blur-sm"></div>
          
          {/* Animated fog particles */}
          {fogParticles.map((i) => (
            <motion.div
              key={`fog-particle-${i}`}
              className="absolute bg-white/40 rounded-full blur-xl"
              style={{
                width: `${Math.random() * 200 + 100}px`,
                height: `${Math.random() * 200 + 100}px`,
                left: `${Math.random() * 120 - 10}%`,
                top: `${Math.random() * 120 - 10}%`,
              }}
              animate={{
                x: [0, Math.random() * 50 - 25],
                y: [0, Math.random() * 30 - 15],
                opacity: [0.2, 0.6, 0.2],
              }}
              transition={{
                duration: 10 + Math.random() * 15,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
      )}
      
      {condition === 'thunderstorm' && showLightning && (
        <div className="absolute inset-0 bg-white/80 z-10"></div>
      )}
    </div>
  );
};

export default WeatherEffects;
